import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useDeletePlansBulk,
  useDeletePlan,
  useGetAllPlans,
  useUpdatePlan,
} from "../../service/api";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Plan } from "../../types";
import { PlanExercisePickerDialog } from "../plan-exercise-picker-dialog";

const DIALOG_PAPER_SX = { backgroundColor: "#fff", color: "#000" };

export const PlansTable = () => {
  const { t } = useTranslation();
  const { data: plans = [] } = useGetAllPlans();
  const { mutate: deletePlan, isPending: deletingPlan } = useDeletePlan();
  const { mutate: deletePlansBulk, isPending: deletingPlansBulk } = useDeletePlansBulk();
  const { mutate: updatePlan, isPending: updatingPlan } = useUpdatePlan();

  const [planToDeleteId, setPlanToDeleteId] = useState<string | null>(null);
  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [editPlanData, setEditPlanData] = useState<Plan | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const visiblePlanIds = plans.map((plan) => plan.id);
  const selectedVisibleCount = selectedPlanIds.filter((id) => visiblePlanIds.includes(id)).length;
  const allVisibleSelected = visiblePlanIds.length > 0 && selectedVisibleCount === visiblePlanIds.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  const confirmDeletePlan = () => {
    if (!planToDeleteId) return;
    deletePlan(planToDeleteId, { onSuccess: () => setPlanToDeleteId(null) });
  };

  const toggleSelectAllPlans = (checked: boolean) => {
    setSelectedPlanIds((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, ...visiblePlanIds]));
      }
      return prev.filter((id) => !visiblePlanIds.includes(id));
    });
  };

  const toggleSelectPlan = (id: string, checked: boolean) => {
    setSelectedPlanIds((prev) => (checked ? Array.from(new Set([...prev, id])) : prev.filter((item) => item !== id)));
  };

  const confirmBulkDeletePlans = () => {
    if (selectedPlanIds.length === 0) return;
    deletePlansBulk(selectedPlanIds, {
      onSuccess: () => {
        setBulkDeleteOpen(false);
        setSelectedPlanIds([]);
      },
    });
  };

  const openEditPlan = (plan: Plan) => {
    setEditingPlan(plan);
    setEditPlanData({ ...plan });
  };

  const handleEditPlanChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditPlanData((prev) => (prev ? { ...prev, [name]: name === "durationInSeconds" ? Number(value) : value } : prev));
  };

  const handleSavePlan = () => {
    if (!editPlanData) return;
    updatePlan({ id: editPlanData.id, data: editPlanData }, {
      onSuccess: () => { setEditingPlan(null); setEditPlanData(null); },
    });
  };

  return (
    <>
      <Dialog open={!!planToDeleteId} onClose={() => setPlanToDeleteId(null)} slotProps={{ paper: { sx: DIALOG_PAPER_SX } }}>
        <DialogTitle sx={{ color: "#000" }}>{t("videoLibrary.plansTable.deleteTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#333" }}>{t("videoLibrary.plansTable.deleteConfirm")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanToDeleteId(null)}>{t("videoLibrary.common.cancel")}</Button>
          <Button onClick={confirmDeletePlan} color="error" variant="contained" disabled={deletingPlan}>
            {deletingPlan ? t("videoLibrary.common.deleting") : t("videoLibrary.common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDeleteOpen} onClose={() => setBulkDeleteOpen(false)} slotProps={{ paper: { sx: DIALOG_PAPER_SX } }}>
        <DialogTitle sx={{ color: "#000" }}>{t("videoLibrary.plansTable.bulkDeleteTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#333" }}>
            {t("videoLibrary.plansTable.bulkDeleteConfirm", { count: selectedPlanIds.length })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)}>{t("videoLibrary.common.cancel")}</Button>
          <Button onClick={confirmBulkDeletePlans} color="error" variant="contained" disabled={deletingPlansBulk}>
            {deletingPlansBulk ? t("videoLibrary.common.deleting") : t("videoLibrary.common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {editPlanData && (
        <PlanExercisePickerDialog
          open={pickerOpen}
          initialSelectedIds={editPlanData.exercisesIds ?? []}
          onClose={() => setPickerOpen(false)}
          onConfirm={(ids) => { setEditPlanData((prev) => (prev ? { ...prev, exercisesIds: ids } : prev)); setPickerOpen(false); }}
        />
      )}

      <Dialog open={!!editingPlan} onClose={() => setEditingPlan(null)} fullWidth maxWidth="sm" slotProps={{ paper: { sx: DIALOG_PAPER_SX } }}>
        <DialogTitle sx={{ color: "#000" }}>{t("videoLibrary.plansTable.editTitle")}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField name="name" label={t("videoLibrary.common.planNameField")} value={editPlanData?.name ?? ""} onChange={handleEditPlanChange} required size="small" sx={{ flex: 2 }} />
            <TextField name="durationInSeconds" label={t("videoLibrary.common.durationSecondsField")} type="number" value={editPlanData?.durationInSeconds ?? 0} onChange={handleEditPlanChange} onFocus={(e) => e.target.select()} required size="small" sx={{ flex: 1 }} />
          </Box>
          <TextField name="description" label={t("videoLibrary.common.descriptionField")} value={editPlanData?.description ?? ""} onChange={handleEditPlanChange} required size="small" />
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField name="category" label={t("videoLibrary.common.categoryField")} value={editPlanData?.category ?? ""} onChange={handleEditPlanChange} required size="small" sx={{ flex: 1 }} />
            <TextField name="level" label={t("videoLibrary.common.levelField")} value={editPlanData?.level ?? ""} onChange={handleEditPlanChange} required size="small" sx={{ flex: 1 }} />
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField name="equipment" label={t("videoLibrary.common.equipmentField")} value={editPlanData?.equipment ?? ""} onChange={handleEditPlanChange} required size="small" sx={{ flex: 1 }} />
            <TextField name="depth" label={t("videoLibrary.common.depthField")} value={editPlanData?.depth ?? ""} onChange={handleEditPlanChange} required size="small" sx={{ flex: 1 }} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button variant="outlined" size="small" onClick={() => setPickerOpen(true)}>
              {t("videoLibrary.common.selectExercises")}
            </Button>
            <Chip label={t("videoLibrary.planForm.selectedCount", { count: editPlanData?.exercisesIds?.length ?? 0 })} size="small" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingPlan(null)}>{t("videoLibrary.common.cancel")}</Button>
          <Button
            onClick={handleSavePlan}
            variant="contained"
            disabled={updatingPlan || !editPlanData?.name.trim() || !editPlanData?.description.trim()}
            sx={{ color: "#fff" }}
          >
            {updatingPlan ? t("videoLibrary.common.saving") : t("videoLibrary.common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper elevation={2} sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", backgroundColor: "#fff", color: "#000" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexShrink: 0, mb: 1 }}>
          <Typography variant="h6" fontWeight="bold">{t("videoLibrary.plansTable.heading")}</Typography>
          <Button
            size="small"
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={() => setBulkDeleteOpen(true)}
            disabled={selectedPlanIds.length === 0}
          >
            {t("videoLibrary.common.deleteSelected", { count: selectedPlanIds.length })}
          </Button>
        </Box>
        <Divider sx={{ mb: 1, flexShrink: 0 }} />
        <TableContainer sx={{ flex: 1, overflow: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    onChange={(e) => toggleSelectAllPlans(e.target.checked)}
                    inputProps={{ "aria-label": t("videoLibrary.common.selectAll") }}
                  />
                </TableCell>
                <TableCell>{t("videoLibrary.plansTable.colName")}</TableCell>
                <TableCell>{t("videoLibrary.plansTable.colLevel")}</TableCell>
                <TableCell>{t("videoLibrary.plansTable.colCategory")}</TableCell>
                <TableCell>{t("videoLibrary.plansTable.colExercises")}</TableCell>
                <TableCell>{t("videoLibrary.plansTable.colDuration")}</TableCell>
                <TableCell padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>{t("videoLibrary.plansTable.empty")}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                plans.map((plan) => (
                  <TableRow key={plan.id} hover selected={selectedPlanIds.includes(plan.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedPlanIds.includes(plan.id)}
                        onChange={(e) => toggleSelectPlan(plan.id, e.target.checked)}
                        inputProps={{ "aria-label": t("videoLibrary.common.selectRow") }}
                      />
                    </TableCell>
                    <TableCell>{plan.name}</TableCell>
                    <TableCell>{plan.level}</TableCell>
                    <TableCell>{plan.category}</TableCell>
                    <TableCell>{plan.exercisesIds?.length ?? 0}</TableCell>
                    <TableCell>{plan.durationInSeconds}</TableCell>
                    <TableCell padding="checkbox">
                      <Box sx={{ display: "flex" }}>
                        <Tooltip title={t("videoLibrary.plansTable.editTooltip")}>
                          <IconButton size="small" onClick={() => openEditPlan(plan)} sx={{ color: "#1976d2" }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("videoLibrary.plansTable.deleteTooltip")}>
                          <IconButton size="small" onClick={() => setPlanToDeleteId(plan.id)} sx={{ color: "#d32f2f" }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};
